import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView, Alert, Image, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { doc, onSnapshot } from 'firebase/firestore';
// SDK NATIVO: en Android el SDK web viaja SIN SESIÓN (el login real vive en
// @react-native-firebase vía authBridge) y la regla de /users exige
// isSelf(uid), así que la lectura se denegaba. Misma rama que usa wallet.tsx.
import nativeFirestore from '@react-native-firebase/firestore';
import { useThemeColors, Colors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { preguntarSiNo } from '../utils/preguntar';
import { db } from '../services/firebaseConfig';
import { paymentAccountsService, type PaymentAccount } from '../services/paymentAccountsService';
import { pesos } from '../utils/dinero';

/** Los mismos tres roles que pagan cuota en wallet.tsx y en el servidor. */
const ROLES_CON_CUOTA = new Set(['DRIVER', 'DELIVERY_DRIVER', 'SCHOOL_DRIVER']);

/** Misma fuente de verdad que wallet.tsx, adaptada a la forma de este modal. */
function toModalBank(a: PaymentAccount) {
  return {
    id: a.id,
    name: a.name,
    account: a.accountNumber,
    type: a.accountType,
    holder: a.holder,
  };
}

interface WalletModalProps {
  visible: boolean;
  onClose: () => void;
}

export default function WalletModal({ visible, onClose }: WalletModalProps) {
  const colors = useThemeColors();
  const router = useRouter();
  const userFirebaseUid = useAppStore((s) => s.userFirebaseUid);

  // ─── EL SALDO SE LEE DE FIRESTORE, COMO EN /wallet ───────────────────────
  // Antes salía de `useAppStore.walletBalance`, y `setWalletBalance` no se
  // llama en NINGÚN sitio del proyecto: este modal enseñaba RD$ 0.00 a todo el
  // mundo, incluido el chofer que acababa de depositar su cuota. Para él eso
  // es "la app me desapareció el dinero".
  //
  // Se repite el listener en vez de compartirlo con wallet.tsx a propósito:
  // el helper común tendría que vivir en services/firestoreService.ts, que en
  // esta ronda lo está tocando otro frente. Cuando se unifique, este bloque y
  // el de wallet.tsx salen de ahí.
  const [balance, setBalance] = useState(0);
  const [saldoAFavor, setSaldoAFavor] = useState(0);
  const [rolDelDocumento, setRolDelDocumento] = useState<string | null>(null);
  const [saldoRecibido, setSaldoRecibido] = useState(false);
  const [errorSaldo, setErrorSaldo] = useState<string | null>(null);
  const esChoferConCuota = !!rolDelDocumento && ROLES_CON_CUOTA.has(rolDelDocumento);
  const saldoListo = !userFirebaseUid || saldoRecibido;

  useEffect(() => {
    // Sin sesión no hay saldo que leer, y el modal ya lo dice con palabras.
    if (!userFirebaseUid) return;

    const aplicar = (data: any) => {
      const w = Number(data?.walletBalance ?? 0);
      setBalance(Number.isFinite(w) ? w : 0);
      const c = Number(data?.subscriptionCredit ?? 0);
      // Nunca negativo: un crédito en rojo no existe como concepto.
      setSaldoAFavor(Number.isFinite(c) && c > 0 ? c : 0);
      setRolDelDocumento(typeof data?.role === 'string' ? data.role : null);
      setErrorSaldo(null);
      setSaldoRecibido(true);
    };
    const fallo = (msg: string) => {
      console.warn('[WalletModal] no se pudo leer el saldo:', msg);
      setErrorSaldo(msg);
      setSaldoRecibido(true);
    };

    if (Platform.OS !== 'web') {
      const unsubNat = nativeFirestore()
        .collection('users')
        .doc(userFirebaseUid)
        .onSnapshot(
          // En @react-native-firebase v24 `exists` es MÉTODO, no propiedad.
          (snap: any) => aplicar(snap?.exists?.() ? snap.data() : null),
          (e: any) => fallo(String(e?.message ?? e))
        );
      return () => unsubNat();
    }
    const unsub = onSnapshot(
      doc(db, 'users', userFirebaseUid),
      (snap) => aplicar(snap.exists() ? snap.data() : null),
      (err) => fallo(String(err?.message ?? err))
    );
    return () => unsub();
  }, [userFirebaseUid]);

  const [rechargeMode, setRechargeMode] = useState(false);

  // FIX AUDIT — Antes este modal SIMULABA la recarga (setWalletBalance local
  // +500 tras 2s) → dinero FALSO que no persistía ni era gastable. Ahora manda
  // al usuario a la pantalla REAL de recarga (/wallet: OCR de comprobante +
  // tarjeta Stripe, que acredita walletBalance server-side).
  const goToRealRecharge = () => {
    onClose();
    router.push('/wallet');
  };
  const [selectedBank, setSelectedBank] = useState<string | null>(null);

  // CUENTAS DE COBRO — misma fuente que wallet.tsx: Firestore,
  // `app_config/payment_accounts`, editable desde la consola de Firebase.
  //   · Se eliminó la cuenta a nombre de un TERCERO ajeno al negocio.
  //   · `holder` viaja con cada cuenta: antes la UI pintaba
  //     "Titular: Go Safely SRL" fijo para TODAS, siendo cuentas personales
  //     — información financiera engañosa e infracción de Play Store.
  const [BANKS, setBANKS] = useState(() => paymentAccountsService.getFallback().map(toModalBank));
  useEffect(() => {
    let alive = true;
    paymentAccountsService
      .getAccounts()
      .then((accounts) => {
        // Igual que en wallet.tsx: una lista vacía es una respuesta válida y
        // hay que respetarla, no taparla con el respaldo incrustado.
        if (alive) setBANKS(accounts.map(toModalBank));
      })
      .catch(() => { /* el respaldo ya está en pantalla */ });
    return () => { alive = false; };
  }, []);

  const handleUploadVoucher = async () => {
    // ⚠️ ESTO ERA UN `Alert.alert` Y EN LA WEB NO HACÍA NADA: en
    // react-native-web `Alert` es una función vacía, así que el `onPress` no
    // corría y quien quería recargar desde la computadora se quedaba mirando
    // un botón muerto. Y éste es el ÚNICO camino a la pantalla de recarga.
    //
    // De paso: los dos botones de antes («Tomar Foto» y «Galería») llamaban a
    // la MISMA función. La elección real se hace en la pantalla de recarga, no
    // aquí, así que ahora el texto dice lo que de verdad va a pasar.
    const seguir = await preguntarSiNo(
      'Subir Comprobante',
      'Te llevamos a la pantalla de recarga. Allí puedes tomarle la foto a tu comprobante de depósito o transferencia, o escogerla de tu galería.',
      'Cancelar',
      'Continuar'
    );
    if (seguir) processVoucher();
  };

  const processVoucher = () => {
    // FIX AUDIT — Ya NO simulamos recarga aquí. La validación real del
    // comprobante (OCR + acreditación server-side) vive en /wallet.
    setRechargeMode(false);
    goToRealRecharge();
  };

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="chevron-down" size={28} color={colors.text} />
          </TouchableOpacity>
          <Text style={[styles.title, { color: colors.text }]}>Mi Billetera</Text>
          <View style={{ width: 28 }} />
        </View>

        <ScrollView contentContainerStyle={styles.content}>
          <View style={styles.balanceCard}>
            {/* Cuatro estados, nunca uno disfrazado de otro. Un "RD$ 0.00"
                pintado porque la lectura falló es peor que no enseñar nada:
                quien depositó cree que le robaron. */}
            <Text style={styles.balanceLabel}>
              {esChoferConCuota ? 'Saldo a favor' : 'Balance Disponible'}
            </Text>
            {!userFirebaseUid ? (
              <>
                <Text style={[styles.balanceAmount, { fontSize: 20 }]}>Sin cuenta todavía</Text>
                <Text style={styles.balanceSub}>
                  Cuando entres con tu cuenta aquí verás tu saldo real.
                </Text>
              </>
            ) : !saldoListo ? (
              <Text style={[styles.balanceAmount, { fontSize: 24 }]}>Cargando…</Text>
            ) : errorSaldo ? (
              <>
                <Text style={[styles.balanceAmount, { fontSize: 20 }]}>No pudimos leer tu saldo</Text>
                <Text style={styles.balanceSub}>
                  Tu dinero está seguro: esto solo impide mostrarlo ahora mismo.
                </Text>
              </>
            ) : esChoferConCuota ? (
              /* Para el chofer la cifra que manda es la que le REBAJA la cuota
                 (`subscriptionCredit`); `walletBalance` es lo que ya depositó.
                 Bajo un solo rótulo parecían lo mismo y no lo son. */
              <>
                <Text style={styles.balanceAmount}>{pesos(saldoAFavor)}</Text>
                <Text style={styles.balanceSub}>Se descuenta de tu membresía del mes</Text>
                {balance > 0 && (
                  <Text style={[styles.balanceSub, { marginTop: 6 }]}>
                    Además llevas {pesos(balance)} depositados
                  </Text>
                )}
              </>
            ) : (
              <>
                <Text style={styles.balanceAmount}>{pesos(balance)}</Text>
                <Text style={styles.balanceSub}>Para pagos de suscripción y comisiones</Text>
              </>
            )}
          </View>

          {!rechargeMode ? (
            <View style={styles.actions}>
              <TouchableOpacity style={styles.rechargeBtn} onPress={goToRealRecharge}>
                <Ionicons name="wallet-outline" size={24} color="#FFF" />
                <Text style={styles.rechargeBtnText}>Recargar Balance</Text>
              </TouchableOpacity>
              
              <View style={styles.subscriptionCard}>
                <View style={styles.subscriptionHeader}>
                  <Ionicons name="calendar" size={20} color="#FF3B30" />
                  <Text style={styles.subscriptionTitle}>Pago de Suscripción</Text>
                </View>
                {/* NO SE DEBITA SOLA, Y NO ES EL DÍA 28.
                    El cálculo del mes corre el DÍA 1 (applyMonthlyCharge, cron
                    `0 1 1 * *`) y el cobro se paga por depósito o
                    transferencia subiendo el comprobante. Prometer un débito
                    automático el 28 hacía que el chofer esperara un cargo que
                    nunca llegaba, y se quedaba sin poder trabajar. */}
                <Text style={styles.subscriptionDesc}>
                  Tu cuota del mes se calcula el <Text style={{fontWeight: 'bold'}}>día 1</Text> y ya viene con tu
                  saldo a favor descontado. Págala por depósito o transferencia y sube tu
                  comprobante para que te la acreditemos.
                </Text>
                
                <View style={styles.freeTripBonusContainer}>
                  <Ionicons name="gift-outline" size={16} color="#00D084" />
                  <Text style={styles.freeTripBonusText}>
                    Si realizas un viaje &quot;Gratis&quot;, el costo se abonará directo a tu suscripción mensual. ¡No pierdes nada!
                  </Text>
                </View>
              </View>
              
              <Text style={[styles.historyTitle, { color: colors.text }]}>Últimos Movimientos</Text>
              {/* Este bloque afirmaba "No hay movimientos recientes" sin haber
                  consultado NADA: se lo decía igual a quien acababa de
                  depositar. Los movimientos reales (recargas y bonos) ya se
                  leen en /wallet; aquí se manda allá en vez de mentir. */}
              <TouchableOpacity style={styles.emptyState} onPress={goToRealRecharge}>
                <Ionicons name="receipt-outline" size={40} color="#CCC" />
                <Text style={styles.emptyText}>Ver tus recargas y bonos</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.rechargeSection}>
              <TouchableOpacity style={styles.backButton} onPress={() => setRechargeMode(false)}>
                <Ionicons name="arrow-back" size={20} color={colors.primary} />
                <Text style={[styles.backText, { color: colors.primary }]}>Volver</Text>
              </TouchableOpacity>

              <Text style={[styles.sectionTitle, { color: colors.text }]}>PAGO DIGITAL (INSTANTÁNEO)</Text>
              <TouchableOpacity 
                style={[styles.bankCard, { backgroundColor: colors.card, borderColor: '#2196F3', borderWidth: 1 }]}
                onPress={() => {
                  Alert.alert('Pasarela de Pago', 'Aquí se conectará la pasarela (Azul, Stripe o CardNET) para procesar recargas automáticas con Visa o Mastercard.');
                }}
              >
                <View style={styles.bankHeader}>
                  <Ionicons name="card" size={24} color="#2196F3" />
                  <View>
                    <Text style={[styles.bankName, { color: colors.text }]}>Tarjeta de Crédito / Débito</Text>
                    <Text style={{ fontSize: 13, color: colors.textSecondary, marginLeft: 10, marginTop: 2 }}>Visa, Mastercard o Amex</Text>
                  </View>
                </View>
              </TouchableOpacity>

              <View style={{ height: 20 }} />

              <Text style={[styles.sectionTitle, { color: colors.text }]}>TRANSFERENCIA O DEPÓSITO (SIN COMISIÓN)</Text>
              <Text style={[styles.sectionDesc, { color: colors.textSecondary }]}>
                Transfiere el monto que deseas recargar a nuestras cuentas oficiales.
              </Text>

              {/* Sin ninguna cuenta encendida se dice, no se tapa con el
                  respaldo incrustado: transferir a una cuenta retirada le
                  cuesta el dinero al usuario. */}
              {BANKS.length === 0 && (
                <View style={[styles.bankCard, { backgroundColor: colors.card }]}>
                  <Text style={[styles.bankDetailText, { color: colors.text, textAlign: 'center' }]}>
                    Ahora mismo no hay ninguna cuenta disponible para transferencias.
                    Vuelve a intentarlo en un rato.
                  </Text>
                </View>
              )}

              {BANKS.map((bank) => (
                <TouchableOpacity 
                  key={bank.id} 
                  style={[
                    styles.bankCard, 
                    { backgroundColor: colors.card },
                    selectedBank === bank.id && styles.bankCardSelected
                  ]}
                  onPress={() => setSelectedBank(bank.id)}
                >
                  <View style={styles.bankHeader}>
                    <Ionicons name={bank.id === 'pagatodo' ? 'cash-outline' : 'business-outline'} size={24} color={colors.primary} />
                    <Text style={[styles.bankName, { color: colors.text }]}>{bank.name}</Text>
                  </View>
                  {selectedBank === bank.id && (
                    <View style={styles.bankDetails}>
                      <Text style={[styles.bankDetailText, { color: colors.text }]}>No.: <Text style={{fontWeight: 'bold'}}>{bank.account}</Text></Text>
                      <Text style={[styles.bankDetailText, { color: colors.text }]}>Tipo: {bank.type}</Text>
                      <Text style={[styles.bankDetailText, { color: colors.text }]}>Titular: {bank.holder}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              ))}

              {selectedBank && (
                <View style={styles.uploadSection}>
                  <Text style={[styles.uploadDesc, { color: colors.text }]}>
                    ¿Ya hiciste el depósito/transferencia?
                  </Text>
                  <TouchableOpacity style={styles.uploadBtn} onPress={handleUploadVoucher}>
                    <Ionicons name="cloud-upload-outline" size={24} color="#FFF" />
                    <Text style={styles.uploadBtnText}>Subir Comprobante</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
        </ScrollView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#EEE',
  },
  closeButton: {
    padding: 5,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  content: {
    padding: 20,
  },
  balanceCard: {
    backgroundColor: '#0A2540',
    padding: 25,
    borderRadius: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
    marginBottom: 30,
  },
  balanceLabel: {
    color: '#A0B2C6',
    fontSize: 14,
    marginBottom: 10,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  balanceAmount: {
    color: '#FFF',
    fontSize: 36,
    fontWeight: '900',
    marginBottom: 5,
  },
  balanceSub: {
    color: '#FFF',
    opacity: 0.6,
    fontSize: 12,
  },
  actions: {
    marginTop: 10,
  },
  rechargeBtn: {
    backgroundColor: '#00D084',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    borderRadius: 15,
    marginBottom: 30,
  },
  rechargeBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 10,
  },
  subscriptionCard: {
    backgroundColor: 'rgba(255, 59, 48, 0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255, 59, 48, 0.2)',
    padding: 15,
    borderRadius: 15,
    marginBottom: 25,
  },
  subscriptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  subscriptionTitle: {
    color: '#FF3B30',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 8,
  },
  subscriptionDesc: {
    color: '#333',
    fontSize: 13,
    lineHeight: 18,
    opacity: 0.8,
  },
  freeTripBonusContainer: {
    flexDirection: 'row',
    backgroundColor: '#E8F5E9',
    padding: 10,
    borderRadius: 8,
    marginTop: 15,
    alignItems: 'center',
  },
  freeTripBonusText: {
    color: '#00D084',
    fontSize: 12,
    fontWeight: 'bold',
    marginLeft: 8,
    flex: 1,
  },
  historyTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 15,
  },
  emptyText: {
    color: '#999',
    marginTop: 10,
    fontSize: 14,
  },
  rechargeSection: {
    marginTop: 5,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  backText: {
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 5,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '900',
    marginBottom: 5,
  },
  sectionDesc: {
    fontSize: 14,
    opacity: 0.7,
    marginBottom: 20,
    lineHeight: 20,
  },
  bankCard: {
    padding: 15,
    borderRadius: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#EEE',
  },
  bankCardSelected: {
    borderColor: '#00D084',
    borderWidth: 2,
  },
  bankHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  bankName: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  bankDetails: {
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#EEE',
  },
  bankDetailText: {
    fontSize: 15,
    marginBottom: 5,
  },
  uploadSection: {
    marginTop: 30,
    padding: 20,
    backgroundColor: 'rgba(0, 208, 132, 0.1)',
    borderRadius: 15,
    alignItems: 'center',
  },
  uploadDesc: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 15,
    textAlign: 'center',
  },
  uploadBtn: {
    backgroundColor: '#0A2540',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderRadius: 12,
    width: '100%',
    justifyContent: 'center',
  },
  uploadBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 10,
  }
});
